import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";
import { useParams, useNavigate } from "react-router-dom";

moment.locale("fr");

const APPOINTMENT_STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
};

const OFFER_STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_OPTIONS = Object.keys(APPOINTMENT_STATUS_LABELS);

export default function AppointmentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [client, setClient] = useState(null);
  const [couturier, setCouturier] = useState(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    async function fetchDetails() {
      setLoading(true);
      const appointSnap = await getDoc(doc(db, "appointments", id));
      if (!appointSnap.exists()) {
        setLoading(false);
        return;
      }
      const appointData = appointSnap.data();
      setAppointment(appointData);
      setNewStatus(appointData.status);

      if (appointData.clientId) {
        const clientSnap = await getDoc(doc(db, "users", appointData.clientId));
        if (clientSnap.exists()) setClient(clientSnap.data());
      }
      if (appointData.couturierId) {
        const couturierSnap = await getDoc(doc(db, "users", appointData.couturierId));
        if (couturierSnap.exists()) setCouturier(couturierSnap.data());
      }
      if (appointData.offerId) {
        const offerSnap = await getDoc(doc(db, "offers", appointData.offerId));
        if (offerSnap.exists()) setOffer(offerSnap.data());
      }
      setLoading(false);
    }
    fetchDetails();
  }, [id]);

  const ProfilePhoto = ({ src, alt }) => {
    if (src)
      return <img src={src} alt={alt} className="w-24 h-24 rounded-full object-cover" />;
    return (
      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-12 h-12"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.982 18.725a7.49 7.49 0 00-11.964 0M15 11.25a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </div>
    );
  };

  const renderStatusLabel = (status, labels, type = "appointment") => {
    const label = labels[status] || status;
    const baseClass = "inline-block px-3 py-1 rounded text-sm font-semibold ";
    const colorClass =
      type === "appointment"
        ? {
            pending: "bg-yellow-100 text-yellow-800",
            confirmed: "bg-green-100 text-green-800",
            waitPayment: "bg-blue-100 text-blue-800",
            paymentConfirmed: "bg-purple-100 text-purple-800",
          }[status] || "bg-gray-100 text-gray-800"
        : {
            pending: "bg-yellow-100 text-yellow-800",
            confirmed: "bg-green-100 text-green-800",
            refused: "bg-red-100 text-red-800",
          }[status] || "bg-gray-100 text-gray-800";

    return <span className={baseClass + colorClass}>{label}</span>;
  };

  const renderOfferContent = () => {
    if (!offer) return <p className="italic text-gray-500">Offre non trouvée</p>;

    if (offer.photos && offer.photos.length > 0) {
      return (
        <div className="grid grid-cols-3 gap-4">
          {offer.photos.map((url, idx) => (
            <img
              key={idx}
              src={url}
              alt={`Photo offre ${idx + 1}`}
              className="w-full h-32 object-cover rounded"
            />
          ))}
        </div>
      );
    }
    // Pas de photos
    if (offer.status === "pending" || offer.status === "confirmed") {
      return renderStatusLabel(offer.status, OFFER_STATUS_LABELS, "offer");
    }
    return renderStatusLabel("refused", OFFER_STATUS_LABELS, "offer");
  };

  const handleStatusChange = async (e) => {
    const val = e.target.value;
    setNewStatus(val);
    setSavingStatus(true);
    try {
      const ref = doc(db, "appointments", id);
      await updateDoc(ref, { status: val });
      setAppointment((prev) => ({ ...prev, status: val }));
    } catch {
      alert("Erreur lors de la mise à jour du statut");
      setNewStatus(appointment.status); // rollback
    }
    setSavingStatus(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Confirmez-vous la suppression de ce rendez-vous ?")) return;
    setDeleting(true);
    try {
      // Mettre l'offre en pending si présente
      if (appointment.offerId) {
        await updateDoc(doc(db, "offers", appointment.offerId), { status: "pending" });
      }
      await deleteDoc(doc(db, "appointments", id));
      alert("Rendez-vous supprimé");
      navigate("/appointments");
    } catch {
      alert("Erreur lors de la suppression");
    }
    setDeleting(false);
  };


  if (!appointment)
    return <PageTemplate title="Détail du rendez-vous" content={<div>Rendez-vous introuvable</div>} />;

  return (
    <PageTemplate loading={loading}
      title="Détail du rendez-vous"
      content={
        <div className="max-w-5xl mx-auto space-y-10 bg-white p-8 rounded shadow font-sans">
          <div className="flex justify-between">
            {/* Client */}
            <div className="w-1/2 pr-6 border-r border-gray-200">
              <h2 className="text-xl font-semibold mb-4">Client</h2>
              <div className="flex items-center space-x-4">
                <ProfilePhoto src={client?.photo} alt="Photo client" />
                <div>
                  <p>
                    <strong>Nom :</strong>{" "}
                    {client ? `${client.firstname} ${client.lastname}` : <span className="italic text-red-600">Utilisateur inexistant</span>}
                  </p>
                  <p><strong>Email :</strong> {client?.email || "-"}</p>
                  <p><strong>Téléphone :</strong> {client?.phoneNumber || "-"}</p>
                </div>
              </div>
            </div>

            {/* Couturier */}
            <div className="w-1/2 pl-6">
              <h2 className="text-xl font-semibold mb-4">Couturier</h2>
              <div className="flex items-center space-x-4">
                <ProfilePhoto src={couturier?.photo} alt="Photo couturier" />
                <div>
                  <p>
                    <strong>Nom :</strong>{" "}
                    {couturier ? `${couturier.firstname} ${couturier.lastname}` : <span className="italic text-red-600">Utilisateur inexistant</span>}
                  </p>
                  <p><strong>Email :</strong> {couturier?.email || "-"}</p>
                  <p><strong>Téléphone :</strong> {couturier?.phoneNumber || "-"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Offre du couturier */}
          <section>
            <h2 className="text-xl font-semibold mb-4">Offre du couturier</h2>
            {renderOfferContent()}
          </section>

          {/* Détails RDV */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold mb-4">Détails du rendez-vous</h2>
            <p>
              <strong>Statut : </strong>
              <select
                value={newStatus}
                onChange={handleStatusChange}
                disabled={savingStatus || deleting}
                className="border rounded px-3 py-1"
                aria-label="Changer statut du rendez-vous"
              >
                {STATUS_OPTIONS.map((key) => (
                  <option key={key} value={key}>
                    {APPOINTMENT_STATUS_LABELS[key]}
                  </option>
                ))}
              </select>
            </p>
            <p><strong>Date RDV :</strong> {appointment.date ? moment(appointment.date).format("LLL") : "-"}</p>
            <p><strong>Date création :</strong> {appointment.createdAt && appointment.createdAt.toDate ? moment(appointment.createdAt.toDate()).format("LLL") : "-"}</p>
            <p><strong>Date paiement :</strong> {appointment.paidAt && appointment.paidAt.toDate ? moment(appointment.paidAt.toDate()).format("LLL") : "-"}</p>
          </section>

          <button
            onClick={handleDelete}
            disabled={savingStatus || deleting}
            className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition"
            aria-label="Supprimer le rendez-vous"
          >
            {deleting ? "Suppression..." : "Supprimer le rendez-vous"}
          </button>
        </div>
      }
    />
  );
}
